import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BaseHttpService } from '@core/http/base-http.service';
import { transformToTagModaByKey } from '@shared/components/tag-field/tag-field.component';
import { EntityName } from '@shared/enums/entity-name.enum';
import {
  AttributeDescription,
  CustomAttributeValue,
  MetaInformation,
} from '@shared/interfaces/attribute-description.interface';
import { PrivateInfo as PrivateChallengeInfo } from '@shared/interfaces/base/base-entity-edit.interface';
import { Category } from '@shared/interfaces/category.interface';
import { ChallengeInterface } from '@shared/interfaces/challenge.interface';
import { CommunityDeltaPayload } from '@shared/interfaces/community.interface';
import {
  JurorRating,
  RecipientJurorQuery,
} from '@shared/interfaces/juror.interface';
import { OrganizationInterface } from '@shared/interfaces/organization.interface';
import { PeopleInterface } from '@shared/interfaces/people.interface';
import { ApiGetResponse } from '@shared/interfaces/responses/ApiResponse.interface';
import { StartPageSettings } from '@shared/interfaces/start-page.interface';
import { TimelineItem } from '@shared/interfaces/timeline-item.interface';
import { UserInterface } from '@shared/interfaces/user.interface';
import {
  ProjectIdeaTemplateInterface,
  VenturePhase,
  VentureRatingForm,
} from '@shared/interfaces/venture.interface';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ChallengeHttpService extends BaseHttpService<ChallengeInterface> {
  constructor(protected http: HttpClient) {
    super(http);
  }

  getApiRootPath(): string {
    return 'challenges';
  }

  saveTimeline(
    challengeId: number,
    payload: Record<string, TimelineItem[]>
  ): Observable<void> {
    const url = `${this.apiUrl}/${challengeId}/timeline`;

    return this.http.put<void>(url, payload);
  }

  /**
   * Partner organizations AND partner challenges — the API applies no type
   * filter. Items come back as organizations (`OrgListItemViewModel`), which is
   * why the response is re-typed away from `ChallengeInterface`.
   */
  getPartners(
    challengeId: number,
    params: Record<string, string | number | boolean>
  ): Observable<ApiGetResponse<OrganizationInterface>> {
    return this.paginateByEntity('/partners', challengeId, params).pipe(
      map(res => {
        this.handlePaginateResponse(res);

        return res as unknown as ApiGetResponse<OrganizationInterface>;
      })
    );
  }

  updatePartnerOrganizations(challengeId, payload) {
    const url = `${this.apiUrl}/${challengeId}/update-partner-org`;

    return this.http.post(url, payload);
  }

  updateChallengeCommunity(
    challengeId: number,
    payload: CommunityDeltaPayload
  ): Observable<void> {
    const url = `${this.apiUrl}/${challengeId}/update-challenge-community`;

    return this.http.post<void>(url, payload);
  }

  saveStartPage(
    challengeId: number,
    payload: Record<string, StartPageSettings>
  ): Observable<void> {
    const url = `${this.apiUrl}/${challengeId}/start-page`;

    return this.http.put<void>(url, payload);
  }

  savePhases(
    challengeId: number,
    payload: Record<string, VenturePhase[]>
  ): Observable<void> {
    const url = `${this.apiUrl}/${challengeId}/phases`;

    return this.http.put<void>(url, payload);
  }

  getPhases(challengeId: number, locale?: string): Observable<VenturePhase[]> {
    const url = `${this.apiUrl}/${challengeId}/phases`;

    const headers = locale
      ? new HttpHeaders({ 'jip-locale': locale })
      : undefined;

    return this.http.get<VenturePhase[]>(url, { headers });
  }

  getSortOptionsById(challengeId: number): Observable<AttributeDescription[]> {
    return this.http.get<AttributeDescription[]>(
      `challenges/${challengeId}/ventures/rating/sort-options`
    );
  }

  getProjectIdeaTemplate(
    challengeId: number,
    creating: boolean,
    locale?: string
  ): Observable<ProjectIdeaTemplateInterface> {
    const url = `${this.apiUrl}/${challengeId}/project-idea-template`;
    const headers = locale
      ? new HttpHeaders({ 'jip-locale': locale })
      : undefined;

    return this.http.get<ProjectIdeaTemplateInterface>(url, {
      params: {
        isCreate: creating,
      },
      headers,
    });
  }

  hasInheritedVentures(challengeId: number): Observable<boolean> {
    return this.http.get<boolean>(
      `${this.apiUrl}/${challengeId}/has-inherited-ventures`
    );
  }

  create(
    dto: ChallengeInterface & { entityCreateFromOrgId?: number }
  ): Observable<ChallengeInterface> {
    if (!dto) {
      return of(null);
    }
    let url = `${this.apiUrl}/${dto.entityCreateFromOrgId ?? ''}`;
    url = url.replace(/\/$/, ''); // Remove trailing slash

    return this.http.post<ChallengeInterface>(url, dto);
  }

  tranformData(org: ChallengeInterface): ChallengeInterface {
    org.expertisesTag = this.getExpertiesTags(org);
    org.keyOfferingTags = this.transformKeyOfferingTags(org);
    return org;
  }

  getAttributeDescription(): Observable<MetaInformation> {
    return super.getAttributeDescription().pipe(
      map((data: MetaInformation) => {
        this.sortTimelineNameToFirst(data);
        return data;
      })
    );
  }

  /*
   * Retrieve all Juror of a challenge
   * */
  getJurors(
    challengeId: number,
    params: HttpParams
  ): Observable<ApiGetResponse<UserInterface>> {
    return this.http.get<ApiGetResponse<UserInterface>>(
      `${this.getApiRootPath()}/${challengeId}/jurors`,
      { params }
    );
  }

  /*
   * Delete jurors
   * */
  deleteJurors(challengeId: number, jurorIds: number[]): Observable<void> {
    const httpOptions = {
      headers: new HttpHeaders(),
      body: jurorIds,
    };

    return this.http.delete<void>(
      `${this.getApiRootPath()}/${challengeId}/jurors`,
      httpOptions
    );
  }

  /*
   * Retrieve all pending Juror of a challenge
   * */
  getPendingJurors(
    challengeId: number,
    params?: HttpParams
  ): Observable<ApiGetResponse<UserInterface>> {
    return this.http.get<ApiGetResponse<UserInterface>>(
      `${this.getApiRootPath()}/${challengeId}/pending-jurors`,
      { params }
    );
  }

  checkJuror(challengeId: number, isPending?: boolean): Observable<boolean> {
    const url = `${this.getApiRootPath()}/${challengeId}/check-juror`;

    if (isPending === undefined) {
      return this.http.get<boolean>(url);
    }

    return this.http.get<boolean>(url, { params: { isPending } });
  }

  /*
   * Accept to be a Juror
   * */
  acceptJurors(challengeId: number): Observable<void> {
    return this.http.put<void>(
      `${this.getApiRootPath()}/${challengeId}/accept/jurors`,
      null
    );
  }

  /*
   * Deny being a Juror
   * */
  denyJurors(challengeId: number): Observable<void> {
    return this.http.put<void>(
      `${this.getApiRootPath()}/${challengeId}/deny/jurors`,
      null
    );
  }

  /*
   * Get Rating Link
   * */
  getRatingLink(challengeId: number): Observable<JurorRating> {
    return this.http.get<JurorRating>(
      `${this.getApiRootPath()}/${challengeId}/jury-rating-link`
    );
  }

  /*
   * Update Rating Link
   * */
  updateRatingLink(challengeId: number, link: JurorRating): Observable<void> {
    return this.http.post<void>(
      `${this.getApiRootPath()}/${challengeId}/jury-rating-link`,
      link
    );
  }

  checkPrivateChallenge(id: number): Observable<PrivateChallengeInfo> {
    const url = `${this.apiUrl}/${id}/check-private`;
    return this.http.get<PrivateChallengeInfo>(url);
  }

  // The { challengeName } body is not in the API spec yet; backend confirmed it will be supported
  duplicate(
    id: number,
    payload: { challengeName: string }
  ): Observable<ChallengeInterface> {
    const url = `${this.apiUrl}/${id}/duplicate`;

    return this.http.post<ChallengeInterface>(url, payload);
  }

  checkDuplicateName(name: string): Observable<unknown> {
    const url = `${this.apiUrl}/check-challenge-name-exist`;

    return this.http.get(url, {
      params: {
        name,
      },
    });
  }

  deactivateChallenge(id) {
    const url = `${this.apiUrl}/${id}/deactivate`;
    return this.http.delete(url);
  }

  getCategories(id: number, entityName?: EntityName): Observable<Category[]> {
    const url = `${this.apiUrl}/${id}/categories`;
    const params = entityName ? { entityName } : {};

    return this.http.get<Category[]>(url, { params });
  }

  deleteJuryForm(id: number) {
    return this.http.delete(`${this.apiUrl}/${id}/delete-jury-form`);
  }

  getProjectIdeaRating(
    id: number,
    params: HttpParams
  ): Observable<ApiGetResponse<VentureRatingForm>> {
    const url = `${this.apiUrl}/${id}/ventures/rating`;
    if (!params) {
      params = new HttpParams();
    }

    return this.http.get<ApiGetResponse<VentureRatingForm>>(url, { params });
  }

  exportJurorRatings(id: number, payload): Observable<HttpResponse<Blob>> {
    const url = `${this.apiUrl}/${id}/rating/export`;

    return this.http.post<Blob>(url, payload, {
      responseType: 'blob' as 'json',
      observe: 'response',
    });
  }

  getSendMessageToJurorOptions(): Observable<CustomAttributeValue[]> {
    const url = `${this.apiUrl}/jurors/message-options`;

    return this.http.get<CustomAttributeValue[]>(url);
  }

  getRecipientJurors(
    id: number,
    params: RecipientJurorQuery
  ): Observable<PeopleInterface[]> {
    const url = `${this.apiUrl}/${id}/jurors/recipients`;

    const httpParams = new HttpParams({
      fromObject: params as Record<string, boolean>,
    });

    return this.http.get<PeopleInterface[]>(url, { params: httpParams });
  }

  private sortTimelineNameToFirst(data): void {
    const timelineMeta = data.entityDescription.attributeDescriptions.find(
      item => item.propertyName === 'ChallengeTimeline'
    );

    if (timelineMeta) {
      const attributes = timelineMeta?.relatedEntityAttributeDescriptions;
      if (attributes) {
        const nameIndex = attributes.findIndex(
          item => item.propertyName === 'Name'
        );
        if (nameIndex > -1) {
          const removedItem = attributes.splice(nameIndex, 1);
          attributes.unshift(removedItem[0]);
        }
      }
    }
  }

  private getExpertiesTags(
    item: ChallengeInterface
  ): { text: string; weight: number }[] {
    if (item.expertises) {
      return item.expertises.map(expertise => {
        return {
          text: expertise.description,
          weight: expertise.priority + 10,
        };
      });
    }
    return [];
  }

  private transformKeyOfferingTags(
    item: ChallengeInterface
  ): { display: string; value: number; readonly?: boolean }[] {
    if (item.keyOfferings) {
      return transformToTagModaByKey(
        item.keyOfferings,
        true,
        'description',
        'description'
      );
    }
    return [];
  }
}
